import { Heading, Input, Stack, Table, Tbody, Td, Text, Tr, useToast } from '@chakra-ui/react';
import { ComponentType, Fragment } from 'react';

import { TruckingWhitelistInput } from '../components/TruckingWhitelistInput';
import { trpc } from '../config/trpc';

type ConfigInputProps = {
  value: string;
  isDisabled?: boolean;
  onSave: (value: string) => Promise<void>;
};

/**
 * Keys whose value is too structured to type into a single line get their own editor, keyed by
 * `<scope>.<name>`. Everything else falls back to the plain input below.
 */
const INPUT_OVERRIDES: Record<string, ComponentType<ConfigInputProps>> = {
  'truckingWhitelist.truckingWhitelistRules': TruckingWhitelistInput,
};

export const GeneralConfiguration = () => {
  const { data: configs, refetch, isLoading: isFetching } = trpc.getAllConfigs.useQuery();
  const { mutateAsync, isLoading: isUpdating } = trpc.updateConfiguration.useMutation();
  const toast = useToast();

  const update = async (scope: string, name: string, value: string) => {
    try {
      await mutateAsync({
        scope,
        name,
        value,
      });
      await refetch();
      toast({
        colorScheme: 'green',
        title: 'Success',
        description: 'Value updated',
      });
    } catch (e) {
      toast({
        colorScheme: 'red',
        title: 'Error',
        description: e?.message ?? 'An error occured',
      });
      // Rethrown so a custom editor can keep the value on screen rather than assume it saved
      throw e;
    }
  };

  return (
    <Stack spacing="2" maxH="100vh" h="full" overflowY="auto" p={4}>
      <Heading variant="h5">General Configuration</Heading>
      <Table variant="compact">
        <Tbody>
          {Object.entries(configs?.configs ?? {}).map(([category, subValues]) => (
            <Fragment key={category}>
              <Tr>
                <Td colSpan={3} fontWeight="bold" fontSize="2xl" p={3}>
                  {category}
                </Td>
              </Tr>
              {Object.entries(subValues).map(([name, value]) => {
                const Override = INPUT_OVERRIDES[`${category}.${name}`];
                const isDisabled = isFetching || isUpdating;

                if (Override) {
                  return (
                    <Tr key={name}>
                      <Td verticalAlign="top">{name}</Td>
                      <Td colSpan={2}>
                        <Stack spacing="1">
                          <Text fontSize="sm" opacity="0.7">
                            {configs.descriptions[category][name]}
                          </Text>
                          <Override
                            value={value}
                            isDisabled={isDisabled}
                            onSave={(next) => update(category, name, next)}
                          />
                        </Stack>
                      </Td>
                    </Tr>
                  );
                }

                return (
                  <Tr key={name}>
                    <Td>{name}</Td>
                    <Td>
                      <Input
                        disabled={isDisabled}
                        name={`${category}.${name}`}
                        defaultValue={value}
                        onBlur={(e) => update(category, name, e.target.value)}
                      />
                    </Td>
                    <Td>{configs.descriptions[category][name]}</Td>
                  </Tr>
                );
              })}
            </Fragment>
          ))}
        </Tbody>
      </Table>
    </Stack>
  );
};
